import Head from "next/head";
import { useRouter } from "next/router";

import { useContext, useEffect, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import PaneLayout from "components/PaneLayout";
import { Tab, Tabs } from "components/Tabs";
import { TopNavBarContext } from "components/TopNavBar/TopNavBarContext";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";

import ToolsPageAll from "./ToolsPageAll";
import ToolsPageLayoutMobile from "./ToolsPageLayoutMobile";
import ToolsPageSectionLabel from "./ToolsPageSectionLabel";
import ToolsPageTabs, { AVAILABLE_TABS } from "./ToolsPageTabs";

const title =
  "Investment Tools like IPO Analyser, Holdco Monitor & more | Smartkarma";
const description =
  "Analyse IPOs, Holding Companies, M&A situations and more using Smartkarma's proprietery investment tools";

type ToolsPageLayoutProps = {};

const ToolsPageLayout = ({}: ToolsPageLayoutProps) => {
  const { setShowBackButton } = useContext(TopNavBarContext);

  const media = useMediaQuery();
  const router = useRouter();
  const tabQuery =
    (router.query.tab as
      | "explore"
      | "markets"
      | "event-driven"
      | "screeners"
      | "insight-providers"
      | "charting") || "explore";

  const tab = useMemo(
    () => AVAILABLE_TABS.find((t) => t !== "separator" && t.id === tabQuery),
    [tabQuery]
  );

  const { account: loggedInAccount } = useAuthentication();
  const { trackActivity } = useActivityTracker();

  const [section, setSection] = useState(
    tab !== "separator" && tab?.segments
      ? `tools-for-${tab?.segments[0].id}`
      : null
  );

  const canonicalUrl =
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath);

  useEffect(() => {
    setShowBackButton(false);
  }, [setShowBackButton]);

  const children = useMemo(() => {
    if (tab !== "separator" && tab?.segments) {
      const segments = tab.segments.filter(
        (s) => !s.permission || loggedInAccount?.[s.permission]
      );
      return (
        <>
          {segments.map((s) => {
            return (
              <div key={s.id} id={`tools-for-${s.id}`} className="">
                <CustomCard className="p-[15px] md:p-[30px] mt-[15px] first-of-type:mt-0">
                  <ToolsPageSectionLabel>{s.name}</ToolsPageSectionLabel>
                  <div className="mt-[15px]">
                    <ToolsPageAll type={s.id} />
                  </div>
                </CustomCard>
              </div>
            );
          })}
        </>
      );
    }

    return (
      <CustomCard className="p-[15px] md:p-[30px] min-h-[calc(100vh-170px)]">
        <ToolsPageAll type={tabQuery} />
      </CustomCard>
    );
  }, [tab, tabQuery, loggedInAccount]);

  const renderTabs = useMemo(() => {
    if (tab !== "separator" && tab?.segments) {
      const segments = tab.segments.filter(
        (s) => !s.permission || loggedInAccount?.[s.permission]
      );

      return (
        <div className="mb-[20px] mt-0 md:mt-[-30px]">
          <Tabs className="p-0 m-0">
            {segments.map((s) => {
              return (
                <Tab
                  active={section === `tools-for-${s.id}`}
                  activeClassName="hover:bg-[#0276be] hover:text-white"
                  onClick={() => {
                    const topPos = document.getElementById(
                      `tools-for-${s.id}`
                    )?.offsetTop;
                    document.documentElement.scrollTop = topPos || 0;
                    setSection(`tools-for-${s.id}`);
                  }}
                  className="!max-w-none"
                  key={s.name}
                >
                  {s.name}
                </Tab>
              );
            })}
          </Tabs>
        </div>
      );
    }
  }, [loggedInAccount, section, tab]);

  useEffect(() => {
    trackActivity("tools.list");
  }, [trackActivity]);

  useEffect(() => {
    if (
      tab !== "separator" &&
      tab?.permission &&
      !loggedInAccount?.[tab?.permission]
    ) {
      router.push("/tools");
    }
  }, [loggedInAccount, router, tab]);

  useEffect(() => {
    const updateTabsPosition = () => {
      const pos = document.documentElement.scrollTop || document.body.scrollTop;

      if (pos === 0 && tab !== "separator") {
        setSection(`tools-for-${tab?.segments?.[0].id}`);
      }
    };

    updateTabsPosition();

    window.addEventListener("scroll", updateTabsPosition, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateTabsPosition);
    };
  }, [media.mobile, tab]);

  const disclaimer = useMemo(() => {
    return (
      <div className="text-[13px]">
        <span className="font-bold">Disclaimer:</span> While all reasonable care
        has been taken in the preparation of the Lab online tools, Smartkarma
        makes no assurance about the accuracy of any generated data or results.
        All data and results generated by the Lab online tools are indicative
        only and should be independently checked for accuracy and confirmed
        before use. Smartkarma accepts no responsibility for any loss or damage
        caused as a result of any inaccuracy or error within the Lab online
        tools or generated data.
      </div>
    );
  }, []);

  return (
    <>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />

        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />

        {/* Open graph tags for social media etc */}
        {/* <meta property="og:image" content={coverImageUrl} /> */}
        <meta property="og:image:width" content="200" />
        <meta property="og:image:height" content="200" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Head>
      {media.mobile ? (
        <ToolsPageLayoutMobile>
          <div className="flex flex-col min-h-[calc(100vh-200px)]">
            {renderTabs}
            <div className="flex-1">{children}</div>
            {disclaimer}
          </div>
        </ToolsPageLayoutMobile>
      ) : (
        <PaneLayout
          className="pl-[30px] py-[20px] min-h-[calc(100vh-170px)]"
          left={<ToolsPageTabs />}
          hideRight
          hideGoToTop
        >
          <div className="flex flex-col min-h-[calc(100vh-170px)]">
            <div
              className={twMerge(
                "mt-[45px] md:pl-[15px]",
                "flex-1",
                "w-full",
                "flex flex-col"
              )}
            >
              {renderTabs}
              <div className="flex-1">{children}</div>
            </div>
            {disclaimer}
          </div>
        </PaneLayout>
      )}
    </>
  );
};

export default ToolsPageLayout;
